import { Badge, Combobox, type ComboboxOption } from '@pcrm/fe/ui-kit';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { customersQueryOptions } from '../customers/customers.api';
import { partnersQueryOptions } from '../partners/partners.api';

/** Cât se așteaptă după ultima tastă înainte de a cere listele. */
const SEARCH_DEBOUNCE_MS = 300;

/**
 * Al cui e actul, cum îl alege ecranul.
 *
 * Uniune discriminată, ca pe server (`ReceiveDocumentOwner`): un act are exact un proprietar,
 * iar la partener firma se alege separat. `name` se poartă pentru ecranele care trebuie să-l
 * scrie fără o a doua cerere.
 */
export type DocumentOwner =
  | { readonly kind: 'customer'; readonly customerId: string; readonly name: string }
  | { readonly kind: 'partner'; readonly partnerId: string; readonly name: string };

/** Cheia de opțiune, ca id-urile celor două liste să nu se calce. */
function optionValue(owner: DocumentOwner): string {
  return owner.kind === 'customer' ? `client:${owner.customerId}` : `partener:${owner.partnerId}`;
}

/**
 * **Un singur câmp pentru „al cui e actul"** — clienți și parteneri la un loc, cu căutare.
 *
 * > Cerut de fondator, 2026-09-18 ([#149](https://github.com/bplugaru/efx-pcrm/issues/149)):
 * > *„aici ar trebui să fie un select cu search"*, *„de ce nu putem căuta simultan și printre
 * > clienți și parteneri și să apară cu un label (asta e partener)"*, *„de ce trebuie să avem
 * > un comutator?"*. Și: *„asta o să fie folosită peste tot"* — de aceea e componentă, nu un
 * > bloc copiat în două formulare.
 *
 * ## Ce a înlocuit, și de ce
 *
 * Trei controale pentru o întrebare: un comutator Client / Partener, un câmp de căutare și un
 * `Select`. ⚠️ **Câmpul de căutare părea mort**, și pe bună dreptate: filtra lista dinăuntrul
 * `Select`-ului, care era închis — la tastare nu se schimba nimic din ce se vedea. Nu era
 * stricat, era așezat greșit.
 *
 * Comutatorul cerea, în plus, un răspuns înaintea întrebării: operatorul cu actul în mână
 * știe **denumirea**, nu categoria din care face parte cine l-a trimis.
 *
 * ## Căutarea e a serverului
 *
 * ⚠️ **`serverFiltered`, obligatoriu aici.** `Combobox` filtrează implicit lista primită;
 * peste rânduri care vin deja filtrate, ar fi scris „Nimic găsit" pentru un client care chiar
 * există — între două tastări, `options` poartă răspunsul pentru termenul dinainte.
 *
 * ⚠️ **Cât timp cererea e pe drum, panoul nu spune „Nimic găsit".** Ar fi același mesaj pentru
 * „n-am găsit" și pentru „încă nu știu", iar al doilea e cazul obișnuit la a treia literă.
 *
 * ## Ce se vede pe rând
 *
 * Denumirea, iar dedesubt: la client codul lui și firma din grup, la partener cuvântul
 * **„Partener"**. ⚠️ **Fără cod fiscal**, nicăieri: la un partener persoană fizică e un CNP,
 * iar ecranul ăsta stă deschis cu teancul de acte pe masă
 * (`architecture/personal-data.md`).
 *
 * ⚠️ **După alegere, câmpul arată doar denumirea** — un `<input>` cu text, nu un rând randat.
 * De aceea insigna „Partener" se desenează **sub** câmp: altfel un partener și un client cu
 * același nume ar arăta identic exact în clipa în care se consumă un număr de registru.
 */
export function OwnerPicker({
  id,
  value,
  onChange,
  disabled = false,
  invalid = false,
}: {
  id: string;
  /** `null` = nimic ales încă. */
  value: DocumentOwner | null;
  onChange: (owner: DocumentOwner | null) => void;
  disabled?: boolean;
  invalid?: boolean;
}) {
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(search.trim()), SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [search]);

  const term = debounced === '' ? {} : { search: debounced };
  // Două liste, o singură întrebare. **Fără endpoint nou**: un al treilea drum de citire ar fi
  // al treilea loc de ținut la zi, iar cele două răspund deja exact la ce se caută aici.
  const customers = useQuery(customersQueryOptions({ page: 1, ...term }));
  const partners = useQuery(partnersQueryOptions({ page: 1, ...term }));

  /**
   * Rândurile, **memorate**.
   *
   * ⚠️ **Nu e o optimizare.** Lista se dă mai departe ca `items`; o identitate nouă la
   * fiecare randare face `Combobox` să-și piardă starea internă — inclusiv **textul tastat**,
   * care se golește singur la prima reîmprospătare. Măsurat: cu lista construită inline,
   * câmpul rămânea gol după ce răspundea serverul.
   *
   * Depinde de ce s-a ales, nu doar de ce a venit: rândul păstrat mai jos face parte din ea.
   */
  const options: ComboboxOption[] = useMemo(() => {
    const rows: ComboboxOption[] = [
      // Clienții primii: sunt cazul obișnuit. Partenerii nu se pierd — panoul derulează, iar
      // căutarea îi aduce oricum sus de la primele litere ale denumirii.
      ...(customers.data?.items ?? []).map((customer) => ({
        value: optionValue({ kind: 'customer', customerId: customer.id, name: customer.name }),
        label: customer.name,
        description: `${customer.nickname} · ${customer.companyName}`,
      })),
      ...(partners.data?.items ?? []).map((partner) => ({
        value: optionValue({ kind: 'partner', partnerId: partner.id, name: partner.name }),
        label: partner.name,
        // Cuvântul, nu o insignă: `description` e text, și **intră în ce se citește pe rând**.
        description: 'Partener',
      })),
    ];

    /**
     * Ce s-a ales, adăugat dacă lipsește din listă.
     *
     * ⚠️ După alegere, operatorul mai poate tasta, iar rândurile noi pot să nu-l mai conțină
     * pe cel ales; fără rândul ăsta, `Combobox` n-ar mai găsi valoarea și **ar goli câmpul** —
     * un formular care uită ce ai ales, fără să spună.
     */
    if (value !== null && !rows.some((row) => row.value === optionValue(value))) {
      rows.unshift({
        value: optionValue(value),
        label: value.name,
        ...(value.kind === 'partner' ? { description: 'Partener' } : {}),
      });
    }

    return rows;
  }, [customers.data, partners.data, value]);

  const selected = value === null ? '' : optionValue(value);

  const searching = customers.isFetching || partners.isFetching;

  return (
    <div className="flex flex-col gap-y-2">
      <Combobox
        id={id}
        value={selected}
        onChange={(next) => {
          const found = options.find((option) => option.value === next);

          if (found === undefined) {
            onChange(null);
            return;
          }

          const [, ownerId] = next.split(':');
          onChange(
            next.startsWith('partener:')
              ? { kind: 'partner', partnerId: ownerId ?? '', name: found.label }
              : { kind: 'customer', customerId: ownerId ?? '', name: found.label },
          );
        }}
        options={options}
        placeholder="Caută după denumire"
        // Trei stări, trei texte: se caută · n-am găsit · scrie ceva. Un „Nimic găsit" pus
        // peste toate trei ar fi o afirmație falsă în două cazuri din trei.
        emptyText={
          searching
            ? 'Se caută…'
            : debounced === ''
              ? 'Scrie o denumire.'
              : 'Nimeni cu numele ăsta.'
        }
        serverFiltered
        onSearchChange={setSearch}
        disabled={disabled}
        invalid={invalid}
        required
      />

      {/* Insigna, sub câmp: vezi nota de sus — câmpul nu poate purta decât text. */}
      {value?.kind === 'partner' ? (
        <div>
          <Badge size="2xsmall" color="purple">
            Partener
          </Badge>
        </div>
      ) : null}
    </div>
  );
}
